// Per-pane chat state hook.
//
// Each ChatPane gets its own useChatAgent(config) instance — three panes,
// three independent message buffers. State lives entirely client-side; we
// only call out to Gemini on free-text messages, never on slash commands.
//
// Lifecycle:
//   1. On mount, push the agent's `welcome` lines as system messages.
//   2. `send()` decides: slash command (local) or free text (Gemini stream).
//   3. `runSlash(name, args)` is the imperative entry point used by the
//      slash-command palette / suggestion-pill clicks.
//   4. Slash commands can fire side-effects via `result.action`; we expose
//      the most recent action through `onAction`, set via `setOnAction`,
//      so the parent shell (TmuxLayout) can wire it to modals/downloads.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AgentConfig, ChatMessage, SlashCommandResult } from './AgentConfig';
import { streamChat } from './geminiClient';

type ActionHandler = (action: NonNullable<SlashCommandResult['action']>) => void;

let messageIdSeq = 0;
function nextId(): string {
  messageIdSeq += 1;
  return `m_${Date.now().toString(36)}_${messageIdSeq}`;
}

function makeMessage(role: ChatMessage['role'], text: string, streaming = false): ChatMessage {
  return {
    id: nextId(),
    role,
    text,
    timestamp: Date.now(),
    streaming,
  };
}

export interface UseChatAgentResult {
  messages: ChatMessage[];
  input: string;
  setInput: (v: string) => void;
  send: () => void;
  isStreaming: boolean;
  /** Programmatic slash trigger — used by quick-palette pills and suggestions. */
  runSlash: (name: string, rawArgs?: string) => void;
  /** Parent registers an action handler here (open-rubik, etc). */
  setOnAction: (handler: ActionHandler | null) => void;
}

export function useChatAgent(config: AgentConfig): UseChatAgentResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  // Action handler is registered imperatively so the parent doesn't have to
  // re-render to wire it up after mount.
  const onActionRef = useRef<ActionHandler | null>(null);
  const setOnAction = useCallback((handler: ActionHandler | null) => {
    onActionRef.current = handler;
  }, []);

  // We track the latest message list in a ref so the async stream loop can
  // read fresh state without restarting on every chunk.
  const messagesRef = useRef<ChatMessage[]>(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // -- Welcome lines on mount -------------------------------------------------
  // Guard against double-mount under React 18 StrictMode.
  const welcomeSeededRef = useRef(false);
  useEffect(() => {
    if (welcomeSeededRef.current) return;
    welcomeSeededRef.current = true;
    if (config.welcome.length === 0) return;
    setMessages(prev => [
      ...prev,
      ...config.welcome.map(line => makeMessage('system', line)),
    ]);
  }, [config]);

  // -- Slash command execution ------------------------------------------------

  const applyResult = useCallback((result: SlashCommandResult) => {
    if (result.output.length > 0) {
      setMessages(prev => [
        ...prev,
        ...result.output.map(line => makeMessage('tool', line)),
      ]);
    }
    if (result.followUpPrompt) {
      setInput(result.followUpPrompt);
    }
    if (result.action && onActionRef.current) {
      onActionRef.current(result.action);
    }
  }, []);

  const runSlash = useCallback((rawName: string, rawArgs = '') => {
    const name = rawName.startsWith('/') ? rawName : `/${rawName}`;
    // Always echo the slash invocation as a user message so the chat reads
    // naturally ("I ran X, here's the result").
    setMessages(prev => [...prev, makeMessage('user', `${name}${rawArgs ? ' ' + rawArgs : ''}`)]);

    // Built-in /help — synthesises a result from the agent's slash list.
    if (name === '/help') {
      const lines = ['Available commands:'];
      lines.push('  /help   — this list');
      for (const cmd of config.slashCommands) {
        const usage = cmd.example ? `  ${cmd.example}` : `  ${cmd.name}`;
        lines.push(`${usage.padEnd(28)} — ${cmd.description}`);
      }
      lines.push('');
      lines.push('Anything else is sent to the agent as free-text.');
      applyResult({ output: lines });
      return;
    }

    const cmd = config.slashCommands.find(c => c.name === name);
    if (!cmd) {
      applyResult({
        output: [
          `unknown command: ${name}`,
          `try /help for the list, or type a question.`,
        ],
      });
      return;
    }

    try {
      const result = cmd.handler(rawArgs);
      applyResult(result);
    } catch (err) {
      applyResult({
        output: [`[error] ${name}: ${err instanceof Error ? err.message : String(err)}`],
      });
    }
  }, [config, applyResult]);

  // -- Free-text -> Gemini ----------------------------------------------------

  const sendToGemini = useCallback(async (userText: string) => {
    // Push user message, then a placeholder agent message we mutate
    // in-place as tokens stream in.
    const userMsg = makeMessage('user', userText);
    const agentMsg = makeMessage('agent', '', true);
    setMessages(prev => [...prev, userMsg, agentMsg]);
    setIsStreaming(true);

    // Snapshot history sent to the model = everything BEFORE this user
    // message (so the user message itself goes in via userText).
    const historyForModel = messagesRef.current.slice();

    try {
      const stream = streamChat(config.systemPrompt, historyForModel, userText);
      let accumulated = '';
      for await (const chunk of stream) {
        accumulated += chunk;
        // Use functional update to merge against the freshest message list —
        // matters if another action fired during streaming.
        setMessages(prev =>
          prev.map(m => (m.id === agentMsg.id ? { ...m, text: accumulated } : m)),
        );
      }
    } finally {
      setMessages(prev =>
        prev.map(m => (m.id === agentMsg.id ? { ...m, streaming: false } : m)),
      );
      setIsStreaming(false);
    }
  }, [config.systemPrompt]);

  const send = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput('');

    if (trimmed.startsWith('/')) {
      // Split into "/name" and "rest".
      const spaceIdx = trimmed.indexOf(' ');
      const name = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx);
      const args = spaceIdx === -1 ? '' : trimmed.slice(spaceIdx + 1).trim();
      runSlash(name, args);
      return;
    }

    void sendToGemini(trimmed);
  }, [input, isStreaming, runSlash, sendToGemini]);

  return {
    messages,
    input,
    setInput,
    send,
    isStreaming,
    runSlash,
    setOnAction,
  };
}
